/**
 * The open reservation form lives in the address (`?rezervace=<id>`) on the two pages that host it, so a printout opened
 * from the form can come back to it: the printout is a page of its own (same tab), "Zpět" steps back in the history, and
 * the page reopens the form from the address. Only the form is remembered — filters and the calendar month are page state.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useRezervaceDetailQuery, type Rezervace } from './api';

export type RezervaceUrlSearch = { rezervace?: number };

export function parseRezervaceUrlSearch(input: Record<string, unknown>): RezervaceUrlSearch {
  const n = typeof input.rezervace === 'string' ? Number(input.rezervace) : input.rezervace;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? { rezervace: n } : {};
}

type Cesta = '/pujcovna/rezervace' | '/pujcovna/prehled';

/**
 * Keeps `?rezervace=` in step with the form open on the page (`otevrenaId`; null = none) and, once on arrival, hands back
 * the reservation named by the address so the page can reopen its form (`obnovit`). Until that restore is settled the
 * address is left alone, so a slow read never wipes the id the user came back with.
 */
export function useRezervaceVUrl(cesta: Cesta, otevrenaId: number | null, obnovit: (r: Rezervace) => void) {
  const navigate = useNavigate();
  const { rezervace: zAdresy } = useSearch({ strict: false }) as RezervaceUrlSearch;
  const pocatek = useRef(zAdresy ?? null);
  const [vyrizeno, setVyrizeno] = useState(pocatek.current == null);
  const detail = useRezervaceDetailQuery(pocatek.current ?? 0, !vyrizeno);

  useEffect(() => {
    if (vyrizeno) return;
    // only a read that has settled: cached data is shown while a refetch runs, and the form is seeded from its row once
    if (detail.data && !detail.isFetching) {
      obnovit(detail.data);
      setVyrizeno(true);
    } else if (detail.isError) {
      setVyrizeno(true);
    }
    // `obnovit` is the page's state setter wrapper; it must not retrigger the restore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vyrizeno, detail.data, detail.isFetching, detail.isError]);

  useEffect(() => {
    if (!vyrizeno || otevrenaId === (zAdresy ?? null)) return;
    void navigate({ to: cesta, search: otevrenaId == null ? {} : { rezervace: otevrenaId }, replace: true });
  }, [vyrizeno, otevrenaId, zAdresy, navigate, cesta]);
}

/** The printouts' "Zpět": one step back when this tab came from the app (the form reopens on the page it was opened from), else the reservations. */
export function useZpetZTisku() {
  const router = useRouter();
  const navigate = useNavigate();
  return () => {
    if (router.history.canGoBack()) router.history.back();
    else void navigate({ to: '/pujcovna/rezervace' });
  };
}
